/**
 * FAQ 3 (accordion) — one normalization step from CMS/API payload → what the UI renders.
 * DB shape: faq row + faq_category[] + faq[] nested as category.faqs
 */

export type Faq3Theme = {
  sectionBg: string;
  brandColor: string;
  indexMuted: string;
  itemBorder: string;
  answerBg: string;
  answerBorder: string;
};

export type Faq3Header = {
  title: string;
  subtitle: string;
  titleColor: string;
  subtitleColor: string;
};

export type Faq3Tab = {
  idleText: string;
  activeText: string;
  activeBg: string;
};

export type Faq3Row = {
  id: number;
  question: string;
  answer: string;
  questionColor: string;
  answerColor: string;
  priority: number;
};

export type Faq3Category = {
  id: number;
  name: string;
  orderIndex: number;
  tab: Faq3Tab;
  items: Faq3Row[];
};

export type Faq3SectionBackdrop = {
  /** Solid fill when no image */
  fallbackBg: string;
  imageUrl: string;
  imageAlt: string;
};

export type Faq3ViewModel = {
  header: Faq3Header;
  theme: Faq3Theme;
  section: Faq3SectionBackdrop;
  categories: Faq3Category[];
};

type RawFaq = {
  id: number;
  question?: string;
  question_text_color?: string;
  answer?: string;
  answer_text_color?: string;
  priority?: number;
  is_active?: number;
  status?: number;
};

type RawCategory = {
  id: number;
  name?: string;
  text_color?: string;
  background_color?: string;
  active_text_color?: string;
  active_background_color?: string;
  order_index?: number;
  status?: boolean;
  faqs?: RawFaq[];
};

const DEFAULT_HEADER: Faq3Header = {
  title: "FAQs",
  subtitle: "",
  titleColor: "#171719",
  subtitleColor: "#171719",
};

const DEFAULT_THEME: Faq3Theme = {
  sectionBg: "#FFFFFF",
  brandColor: "#130554",
  indexMuted: "#C5C5C5",
  itemBorder: "#E0E0E0",
  answerBg: "#F8F8F8",
  answerBorder: "#C5C5C5",
};

function faqRowActive(f: RawFaq): boolean {
  if (f.is_active === 0) return false;
  if (f.status === 0) return false;
  return true;
}

function mapFaq(f: RawFaq): Faq3Row {
  return {
    id: f.id,
    question: f.question ?? "",
    answer: f.answer ?? "",
    questionColor: f.question_text_color?.trim() ? f.question_text_color : "#171719",
    answerColor: f.answer_text_color || "#373737",
    priority: f.priority ?? 0,
  };
}

function mapCategory(c: RawCategory): Faq3Category | null {
  if (c.status === false) return null;
  const items = (c.faqs ?? [])
    .filter(faqRowActive)
    .map(mapFaq)
    .sort((a, b) => a.priority - b.priority);
  return {
    id: c.id,
    name: c.name ?? "Category",
    orderIndex: c.order_index ?? 0,
    tab: {
      idleText: c.text_color || "#FFFFFF",
      activeText: c.active_text_color || "#DB5803",
      activeBg: c.active_background_color || "#FFFFFF",
    },
    items,
  };
}

type RawWrapper = { data?: Record<string, unknown> };

/**
 * Accepts the widget prop: `{ data: { ...section fields, categories } }` or already-unwrapped section.
 */
export function buildFaq3ViewModel(input: RawWrapper | Record<string, unknown> | null | undefined): Faq3ViewModel {
  const section =
    input && typeof input === "object" && "data" in input && (input as RawWrapper).data
      ? ((input as RawWrapper).data as Record<string, unknown>)
      : (input as Record<string, unknown>) ?? {};

  const {
    title = DEFAULT_HEADER.title,
    title_text_color: titleColor = DEFAULT_HEADER.titleColor,
    title_1: subtitle = "",
    title_1_color: subtitleColor = DEFAULT_HEADER.subtitleColor,
    section_background_color: sectionBg = DEFAULT_THEME.sectionBg,
    section_background_image: bgImage = "",
    section_background_image_alt: bgImageAlt = "",
    background_color: brandColor = DEFAULT_THEME.brandColor,
    accordion_muted_index_color: indexMuted = DEFAULT_THEME.indexMuted,
    accordion_item_border_color: itemBorder = DEFAULT_THEME.itemBorder,
    accordion_answer_surface_bg: answerBg = DEFAULT_THEME.answerBg,
    accordion_answer_surface_border: answerBorder = DEFAULT_THEME.answerBorder,
    categories: rawCats = [],
  } = section as {
    title?: string;
    title_text_color?: string;
    title_1?: string;
    title_1_color?: string;
    section_background_color?: string;
    section_background_image?: string;
    section_background_image_alt?: string;
    background_color?: string;
    accordion_muted_index_color?: string;
    accordion_item_border_color?: string;
    accordion_answer_surface_bg?: string;
    accordion_answer_surface_border?: string;
    categories?: RawCategory[];
  };

  const categories = (rawCats as RawCategory[])
    .map(mapCategory)
    .filter((c): c is Faq3Category => c != null)
    .sort((a, b) => a.orderIndex - b.orderIndex);

  const imageUrl = typeof bgImage === "string" ? bgImage.trim() : "";
  return {
    header: { title, subtitle, titleColor, subtitleColor },
    theme: { sectionBg, brandColor, indexMuted, itemBorder, answerBg, answerBorder },
    section: {
      fallbackBg: sectionBg,
      imageUrl,
      imageAlt: (typeof bgImageAlt === "string" ? bgImageAlt : "").trim(),
    },
    categories,
  };
}
